import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../api';
import { BRAND_NAME } from '../brand';
import Modal from '../components/Modal';
import HeaderSelect from '../components/HeaderSelect';
import ClientProfileModal from '../components/ClientProfileModal';
import InlinePaymentDialog from '../components/InlinePaymentDialog';
import { paymentMethodLabel } from '../utils/paymentMethod';

const PAYMENT_EXPIRE_MINUTES = 60;

// Brand-палитра (см. styles.css :root)
const BRAND_GREEN = '#1C3D2E';
const CORAL = '#B0603C';
const CORAL_BG = '#FFE7DD';
const AMBER = '#7E5D00';
const AMBER_BG = '#F9EBCF';
const GRAY_TEXT = '#5E6561';
const BORDER = '#EBEDEC';
const SECTION_BG = '#F9F9F6';

const STATUS_OPTIONS = [
  { value: 'pending_payment', label: 'Не выпущенные' },
  { value: 'active', label: 'Активные' },
  { value: 'activated', label: 'Активированные' },
  { value: 'expired', label: 'Истекшие' },
  { value: 'cancelled', label: 'Отозванные' },
];

// Бейдж статуса оплаты — 1:1 как в OrdersPage (paymentStatusLabels).
// Не выдумываем новые лейблы/классы — используем общий source-of-truth.
const PAYMENT_BADGE = {
  pending:      { text: 'Ожидает',           cls: 'pending'   },
  paid:         { text: 'Оплачен',           cls: 'completed' },
  paid_chat:    { text: 'Оплачен',           cls: 'completed' },
  failed:       { text: 'Ошибка оплаты',     cls: 'cancelled' },
  expired:      { text: 'Истёк срок оплаты', cls: 'cancelled' },
  cancelled:    { text: 'Отменён',           cls: 'cancelled' },
  not_required: { text: 'Не требуется',      cls: 'info'      },
};

// Иконочная логика — один в один как в OrdersPage:580-585.
function paymentIconFor(payment_status) {
  const cls = PAYMENT_BADGE[payment_status]?.cls || 'pending';
  const ps = payment_status;
  if (cls === 'paid' || cls === 'paid_chat') return '/icons/nv-payment-paid.svg';
  if (ps === 'cancelled') return '/icons/nv-payment-cancelled.svg';
  if (cls === 'pending') return '/icons/nv-payment-expired.svg';
  if (cls === 'expired' || cls === 'failed') return '/icons/nv-payment-expired.svg';
  if (cls === 'not_required') return '/icons/nv-payment-not-required.svg';
  return null;
}

// Бейдж статуса сертификата (lifecycle, не связан с оплатой).
// status='cancelled' считаем «админ-отменой» только если оплата прошла; иначе отмена
// — следствие просрочки оплаты и отображается в payment-бейдже «Истёк срок оплаты».
function getCertStatusBadge(c) {
  const paid = c.payment_status === 'paid' || c.payment_status === 'paid_chat';
  // Сертификат создан, но не оплачен → не выпущен (не привязано к платежу).
  if (!paid && c.status === 'pending_payment') {
    return { text: 'Не выпущен', cls: 'pending', icon: '/icons/nv-status-new.svg' };
  }
  if (!paid) return null;
  if (c.status === 'active')    return { text: 'Активен',              cls: 'paid',      icon: '/icons/nv-status-completed.svg' };
  if (c.status === 'activated') return { text: 'Использован',          cls: 'activated', icon: '/icons/nv-gift-package.svg' };
  if (c.status === 'expired')   return { text: 'Истёк срок активации', cls: 'expired',   icon: '/icons/nv-payment-expired.svg' };
  if (c.status === 'cancelled') return { text: 'Отменён',              cls: 'cancelled', icon: '/icons/nv-status-cancelled.svg' };
  return null;
}

// PAYMENT_METHODS удалён — InlinePaymentDialog использует общий дефолт
// ('QR' / 'Карта' / 'Наличные' / 'Р/с'), как в OrderModal.

function fmt(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return '—';
  }
}

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return '—';
  }
}

function userLabel(u) {
  if (!u) return '—';
  const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
  const handle = u.username ? `@${u.username}` : null;
  const idPart = u.telegram_id ? `tg:${u.telegram_id}` : (u.max_id ? `max:${u.max_id}` : `#${u.id}`);
  return [name || idPart, handle].filter(Boolean).join(' ');
}

const PLATFORM_BADGE = {
  telegram: { label: 'TG',    bg: '#DCF4E0', color: '#1F763B' },
  max:      { label: 'Max',   bg: '#DEEFFF', color: '#26689A' },
  web:      { label: 'Web',   bg: '#F9EBCF', color: '#9A4820' },
  guest:    { label: 'Гость', bg: '#F4F4EF', color: '#616161' },
};

function StatusPill({ badge }) {
  if (!badge) return null;
  return (
    <span className={`badge badge-${badge.cls || 'pending'}`}>
      {badge.icon && <img src={badge.icon} alt="" />}
      {badge.text}
    </span>
  );
}

// Бренд-секция (как в OrderModal): цветной border-left + светлый фон тоном.
// tone='cancel' → коралл (предупреждение/отмена). По умолчанию — primary green.
function Section({ label, children, style, tone }) {
  const isWarn = tone === 'cancel';
  const accent = isWarn ? CORAL : BRAND_GREEN;
  const bg = isWarn ? CORAL_BG : '#DFF2E8';  // как у заказа: light green
  return (
    <div style={{
      padding: '12px 14px',
      background: bg,
      '--ch': '8px',
      borderLeft: `3px solid ${accent}`,
      ...style,
    }}>
      <div style={{
        fontSize: 11, color: accent, fontWeight: 700,
        marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px',
      }}>
        {label}
      </div>
      {children}
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '3px 0', fontSize: 13 }}>
      <span style={{ color: '#666' }}>{label}</span>
      <span style={{ textAlign: 'right', color: '#222' }}>{value}</span>
    </div>
  );
}

// Карандаш-иконка для toggle-редактирования поля.
function PencilButton({ active, disabled, onClick, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        background: active ? `${BRAND_GREEN}14` : 'transparent',
        border: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
        padding: 4, '--ch': '4px', color: active ? BRAND_GREEN : GRAY_TEXT,
        opacity: disabled ? 0.4 : 1,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
      </svg>
    </button>
  );
}

const FIELD_LABEL = {
  sender_name: 'От кого',
  recipient_name: 'Получатель',
  message: 'Поздравление',
};

// Строка-поле с карандашиком: read-only по умолчанию, клик на карандаш — inline-input.
// editable=false → карандаш скрыт, всегда read-only.
// Single-line поле в стиле KV (label слева, value справа), с опциональным карандашом
// для inline-редактирования. multiline/italic — fallback на старый layout (для будущего).
function FieldRow({ label, value, editing, editValue, onEditChange, onTogglePencil, editable, multiline, italic, charCount }) {
  // Multi-line режим: для редактирования больших полей оставляем layout с label сверху.
  if (multiline) {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <span style={{ color: '#666', fontSize: 13 }}>{label}</span>
          {editable && (
            <PencilButton active={editing} onClick={onTogglePencil}
              title={editing ? 'Отменить' : 'Редактировать'} />
          )}
        </div>
        {editing ? (
          <div>
            <textarea
              value={editValue}
              onChange={(e) => onEditChange(e.target.value)}
              rows={3}
              style={{ ...editInputStyle, resize: 'vertical', fontFamily: 'inherit' }}
              placeholder="—" autoFocus
            />
            {charCount && (
              <div style={{ fontSize: 11, color: '#999', textAlign: 'right' }}>{charCount}</div>
            )}
          </div>
        ) : (
          <div style={{
            fontSize: italic ? 13 : 14, color: italic ? '#5D4037' : '#222',
            fontStyle: italic && value ? 'italic' : 'normal',
            lineHeight: italic ? 1.5 : 1.3, wordBreak: 'break-word',
          }}>
            {value ? (italic ? `«${value}»` : value) : <span style={{ color: '#bbb' }}>—</span>}
          </div>
        )}
      </div>
    );
  }

  // Single-line: «Label: Value [✏]» — всё прижато слева, без растяжки.
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0', fontSize: 13, flexWrap: 'wrap' }}>
      <span style={{ color: '#666' }}>{label}:</span>
      {editing ? (
        <input
          type="text"
          value={editValue}
          onChange={(e) => onEditChange(e.target.value)}
          maxLength={200}
          style={{ ...editInputStyle, padding: '4px 8px', fontSize: 13, width: 240 }}
          placeholder="—"
          autoFocus
          onKeyDown={(e) => { if (e.key === 'Escape') onTogglePencil(); }}
        />
      ) : (
        <span style={{ color: '#222', wordBreak: 'break-word' }}>
          {value || <span style={{ color: '#bbb' }}>—</span>}
        </span>
      )}
      {editable && (
        <PencilButton active={editing} onClick={onTogglePencil}
          title={editing ? 'Отменить' : 'Редактировать'} />
      )}
    </div>
  );
}

export function CertificateDetailModal({ certId, onClose, showToast, onUpdated }) {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [clientModalId, setClientModalId] = useState(null);

  // Inline-редактирование полей (только для оплаченного, не активированного — status='active').
  // editing — Set имён полей в режиме редактирования. По умолчанию все read-only;
  // карандашик переключает поле в активное.
  const [edit, setEdit] = useState({ sender_name: '', recipient_name: '', message: '' });
  const [editing, setEditing] = useState(() => new Set());
  const editDirty = editing.size > 0;

  // Admin comment — отдельная заметка для внутренних нужд, доступна в любом статусе.
  const [adminComment, setAdminComment] = useState('');
  const [adminCommentSaved, setAdminCommentSaved] = useState('');
  const adminCommentDirty = adminComment !== adminCommentSaved;

  // Модалка отмены — обязательная причина
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  // Escape закрывает только окно отмены — карточка сертификата под ним остаётся
  useEffect(() => {
    if (!cancelOpen) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      setCancelOpen(false);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [cancelOpen]);

  // Inline-форма приёма оплаты — состояние формы внутри InlinePaymentDialog.
  const [payOpen, setPayOpen] = useState(false);

  const loadHistory = useCallback(async () => {
    try {
      const rows = await api.getCertificateHistory(certId);
      setHistory(rows || []);
    } catch (e) {
      // Если endpoint вернул 404 (старый бэк до этой задачи) — просто игнорируем.
      setHistory([]);
    }
  }, [certId]);

  const refresh = useCallback(async () => {
    const fresh = await api.getCertificate(certId);
    setData(fresh);
    setEdit({
      sender_name: fresh.sender_name || '',
      recipient_name: fresh.recipient_name || '',
      message: fresh.message || '',
    });
    setEditing(new Set());
    setAdminComment(fresh.admin_comment || '');
    setAdminCommentSaved(fresh.admin_comment || '');
    await loadHistory();
    return fresh;
  }, [certId, loadHistory]);

  useEffect(() => {
    api.getCertificate(certId)
      .then((fresh) => {
        setData(fresh);
        setEdit({
          sender_name: fresh.sender_name || '',
          recipient_name: fresh.recipient_name || '',
          message: fresh.message || '',
        });
        setAdminComment(fresh.admin_comment || '');
        setAdminCommentSaved(fresh.admin_comment || '');
      })
      .catch(e => showToast(e.message || 'Ошибка', 'error'))
      .finally(() => setLoading(false));
    loadHistory();
  }, [certId, showToast, loadHistory]);

  // Редактирование разрешено только для оплаченного, не-активированного сертификата.
  // Pending — клиент сам ввёл данные, ещё не оплатил → не трогаем.
  // Activated/expired/cancelled — финальный статус → не трогаем.
  const editable = data && data.status === 'active';

  const handleEditChange = (field, value) => {
    setEdit(prev => ({ ...prev, [field]: value }));
  };

  const togglePencil = (field) => {
    setEditing(prev => {
      const next = new Set(prev);
      if (next.has(field)) {
        // Закрытие пенсила без сохранения — откатываем значение к серверному.
        next.delete(field);
        setEdit(e => ({ ...e, [field]: data[field] || '' }));
      } else {
        next.add(field);
      }
      return next;
    });
  };

  const cancelAllEdits = () => {
    if (!data) return;
    setEdit({
      sender_name: data.sender_name || '',
      recipient_name: data.recipient_name || '',
      message: data.message || '',
    });
    setEditing(new Set());
  };

  const handleSaveEdit = async () => {
    if (edit.message && edit.message.length > 300) {
      showToast('Поздравление слишком длинное (макс. 300)', 'error');
      return;
    }
    setBusy(true);
    try {
      await api.editCertificate(certId, {
        sender_name: edit.sender_name,
        recipient_name: edit.recipient_name,
        message: edit.message,
      });
      showToast('Изменения сохранены');
      await refresh();
      onUpdated?.();
    } catch (e) {
      showToast(e.message || 'Ошибка сохранения', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleSaveAdminComment = async () => {
    setBusy(true);
    try {
      await api.editCertificate(certId, { admin_comment: adminComment });
      setAdminCommentSaved(adminComment);
      showToast('Комментарий сохранён');
      onUpdated?.();
    } catch (e) {
      showToast(e.message || 'Ошибка сохранения', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleConfirmCancel = async () => {
    const reason = cancelReason.trim();
    if (!reason) {
      showToast('Укажите причину отмены', 'error');
      return;
    }
    setBusy(true);
    try {
      await api.cancelCertificate(certId, reason);
      showToast('Сертификат отозван');
      setCancelOpen(false);
      setCancelReason('');
      await refresh();
      onUpdated?.();
    } catch (e) {
      showToast(e.message || 'Ошибка отмены', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleResend = async () => {
    setBusy(true);
    try {
      await api.resendCertificate(certId);
      showToast('Сертификат отправлен в чат');
      await refresh();
      onUpdated?.();
    } catch (e) {
      showToast(e.message || 'Ошибка отправки', 'error');
    } finally {
      setBusy(false);
    }
  };

  // handleConfirmPayment удалён — логика приёма оплаты живёт в onConfirm
  // у <InlinePaymentDialog/> (см. блок «Оплата»).

  const paymentBadge = data ? PAYMENT_BADGE[data.payment_status] : null;
  const certBadge = data ? getCertStatusBadge(data) : null;

  // Цвет акцента секции «Оплата» зависит от статуса
  const paidColor = (() => {
    if (!data) return '#D3A842';
    if (data.payment_status === 'paid' || data.payment_status === 'paid_chat') return '#1F763B';
    if (data.payment_status === 'expired' || data.payment_status === 'failed' || data.payment_status === 'cancelled') return '#CB4D45';
    return '#D3A842';
  })();

  // Источник платформы покупателя (telegram/max/web)
  const platform = data?.purchased_by?.telegram_id ? 'telegram'
    : data?.purchased_by?.max_id ? 'max'
    : 'web';
  const platformBadge = PLATFORM_BADGE[platform];
  const buyer = data?.purchased_by;
  const activator = data?.activated_by;
  const activatorPlatform = activator?.telegram_id ? 'telegram'
    : activator?.max_id ? 'max'
    : 'web';
  const activatorBadge = PLATFORM_BADGE[activatorPlatform];
  const isPaid = data && (data.payment_status === 'paid' || data.payment_status === 'paid_chat');

  // Действия (footer Modal-а): «Отозвать» — слева, остальные действия — справа.
  const footerActions = data && (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
      <div>
        {(data.status === 'pending_payment' || data.status === 'active') && (
          <button onClick={() => setCancelOpen(true)} disabled={busy} className="btn btn-danger-outline btn-sm">
            Отозвать
          </button>
        )}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {(data.status === 'active' || data.status === 'activated') && data.code && (
          <a href={data.image_url || `/api/certificates/${data.id}/image`} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm">
            Открыть PNG
          </a>
        )}
        {data.status === 'active' && (
          <button onClick={handleResend} disabled={busy} className="btn btn-primary btn-sm">
            Отправить заново в чат
          </button>
        )}
      </div>
    </div>
  );

  return (
    <Modal
      title={
        <>
          Сертификат №{data?.cert_number || certId}
          {' '}
          <span style={{ color: '#1C3D2E', fontWeight: 600, fontSize: 20 }}>
            · {data?.created_at ? fmt(data.created_at) : ''}
          </span>
        </>
      }
      size="large"
      onClose={onClose}
      // Открыта оплата или не сохранён комментарий — Escape и клик мимо спрашивают
      confirmOverlayClose={(payOpen && 'Оплата не отмечена до конца. Закрыть карточку сертификата?')
        || (adminCommentDirty && 'Комментарий не сохранён. Закрыть без сохранения?')}
      dirty={payOpen || adminCommentDirty}
      footer={footerActions}
    >
      <>
        {loading && <div style={{ padding: 20, color: GRAY_TEXT }}>Загрузка...</div>}

        {data && (
          <div style={{ display: 'grid', gap: 10 }}>

            {/* ── Клиент + Активация (2 колонки, точь-в-точь как Заказчик+Доставка у заказа) ── */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {/* Клиент */}
              <div style={{ padding: '10px 12px', background: '#DFF2E8', '--ch': '6px', borderLeft: '3px solid #1C3D2E' }}>
                <div style={{ fontSize: 11, color: '#1C3D2E', fontWeight: 700, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Клиент
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 2, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  {(() => {
                    const name = [buyer?.first_name, buyer?.last_name].filter(Boolean).join(' ').trim() || '—';
                    if (buyer?.id) {
                      return (
                        <span
                          onClick={() => setClientModalId(buyer.id)}
                          style={{ cursor: 'pointer', borderBottom: '1px dashed #1C3D2E', color: '#1C3D2E' }}
                          title="Открыть карточку клиента"
                        >{name}</span>
                      );
                    }
                    return <span>{name}</span>;
                  })()}
                  <span className={`platform-badge ${platform === 'telegram' ? 'platform-tg' : platform === 'max' ? 'platform-max' : 'platform-web'}`}>
                    <img src={`/icons/nv-${platform === 'telegram' ? 'tg' : platform === 'max' ? 'max' : 'web'}.svg`} alt="" />
                    {platform === 'telegram' ? 'TG' : platform === 'max' ? 'Max' : 'Web'}
                  </span>
                </div>
                {buyer?.username && (
                  <div style={{ fontSize: 12, color: '#888' }}>@{buyer.username}</div>
                )}
                {(buyer?.telegram_id || buyer?.max_id) && (
                  <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
                    {buyer?.telegram_id && <span>TG: {buyer.telegram_id}</span>}
                    {buyer?.telegram_id && buyer?.max_id && <span> · </span>}
                    {buyer?.max_id && <span>Max: {buyer.max_id}</span>}
                  </div>
                )}
                {data.phone && (
                  <div style={{ fontSize: 13, marginTop: 2 }}>
                    <a href={`tel:${data.phone}`} style={{ color: '#3A83BB', textDecoration: 'none' }}>{data.phone}</a>
                  </div>
                )}
                {data.email && (
                  <div style={{ fontSize: 12 }}>
                    <a href={`mailto:${data.email}`} style={{ color: '#3A83BB', textDecoration: 'none' }}>{data.email}</a>
                  </div>
                )}
              </div>

              {/* Активация (где у заказа Доставка) */}
              <div style={{ padding: '10px 12px', background: '#DFF2E8', '--ch': '6px', borderLeft: '3px solid #1C3D2E' }}>
                <div style={{ fontSize: 11, color: '#1C3D2E', fontWeight: 700, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Активация
                </div>
                {data.status === 'activated' ? (
                  <>
                    <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>
                      Активирован {data.activated_at && <span style={{ color: '#666', fontWeight: 400 }}>· {fmt(data.activated_at)}</span>}
                    </div>
                    {activator && (
                      <div style={{ fontSize: 13, marginTop: 2 }}>
                        {[activator.first_name, activator.last_name].filter(Boolean).join(' ') || '—'}
                        {activator.username && (
                          <span style={{ color: '#888', marginLeft: 6, fontSize: 12 }}>@{activator.username}</span>
                        )}
                        {activatorPlatform && (
                          <span className={`platform-badge ${activatorPlatform === 'telegram' ? 'platform-tg' : activatorPlatform === 'max' ? 'platform-max' : 'platform-web'}`}
                                style={{ marginLeft: 6 }}>
                            <img src={`/icons/nv-${activatorPlatform === 'telegram' ? 'tg' : activatorPlatform === 'max' ? 'max' : 'web'}.svg`} alt="" />
                            {activatorPlatform === 'telegram' ? 'TG' : activatorPlatform === 'max' ? 'Max' : 'Web'}
                          </span>
                        )}
                      </div>
                    )}
                    {activator?.phone && (
                      <div style={{ fontSize: 13, marginTop: 2 }}>
                        <a href={`tel:${activator.phone}`} style={{ color: '#3A83BB', textDecoration: 'none' }}>{activator.phone}</a>
                      </div>
                    )}
                    {data.bonus_transaction && (
                      <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                        Транзакция #{data.bonus_transaction.id} · +{data.bonus_transaction.amount}₽ · баланс: {data.bonus_transaction.balance_after}
                      </div>
                    )}
                  </>
                ) : data.status === 'expired' ? (
                  <>
                    <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 2, color: '#CB4D45' }}>
                      Истёк срок активации
                    </div>
                    {data.expires_at && (
                      <div style={{ fontSize: 13, color: '#666' }}>
                        Действовал до {fmtDate(data.expires_at)}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 2, color: '#666' }}>
                      Ещё не активирован
                    </div>
                    {data.expires_at && (
                      <div style={{ fontSize: 13, color: '#666' }}>
                        Действует до {fmtDate(data.expires_at)}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* ── ОПЛАТА (full-width как у заказа, нейтральный фон) ── */}
            <div style={{ padding: '10px 12px', '--ch': '6px', borderLeft: '3px solid #1C3D2E', background: '#F4F4EF' }}>
              <div style={{ fontSize: 11, color: '#1C3D2E', fontWeight: 700, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Оплата</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6, flexWrap: 'wrap' }}>
                {(() => {
                  const ps = data.payment_status;
                  const isPaid = ps === 'paid' || ps === 'paid_chat';
                  const isPending = ps === 'pending';
                  const color = isPaid ? '#1C3D2E' : ps === 'cancelled' ? '#5E6561' : '#B0603C';
                  // Базой таймера предпочитаем payment_url_generated_at (момент создания YK-ссылки),
                  // fallback на created_at для легаси-сертификатов без поля.
                  const timerBase = data.payment_url_generated_at || data.created_at;
                  let label;
                  if (isPaid) {
                    const pm = (data.payment_method || '').replace(/\s*\(.*\)\s*$/, '').trim();
                    label = pm ? `Оплачен · ${paymentMethodLabel(pm)}` : 'Оплачен';
                  } else if (isPending && timerBase) {
                    const created = new Date(timerBase).getTime();
                    const expire = created + PAYMENT_EXPIRE_MINUTES * 60 * 1000;
                    const remainingMs = expire - Date.now();
                    if (remainingMs > 0) {
                      const mins = Math.ceil(remainingMs / 60000);
                      label = `Ожидает · ${mins} мин`;
                    } else {
                      label = 'Ожидает · истекает';
                    }
                  } else {
                    label = paymentBadge?.text || 'Ожидает';
                  }
                  return <span style={{ fontSize: 14, fontWeight: 700, color }}>{label}</span>;
                })()}
                {/* Прометный таймер-бейдж для pending — как в реестре заказов/сертификатов */}
                {data.payment_status === 'pending' && (data.payment_url_generated_at || data.created_at) && (() => {
                  const base = new Date(data.payment_url_generated_at || data.created_at).getTime();
                  const remainingMs = base + PAYMENT_EXPIRE_MINUTES * 60 * 1000 - Date.now();
                  const mins = Math.ceil(remainingMs / 60000);
                  return (
                    <span className="badge badge-info">
                      <img src="/icons/clock.svg" alt="" />
                      {remainingMs > 0 ? `${mins} мин` : 'истекает'}
                    </span>
                  );
                })()}
                {data.paid_at && (
                  <span style={{ fontSize: 13, color: '#5E6561' }}>
                    {new Date(data.paid_at).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
                {data.payment_status === 'pending' && (
                  <button
                    type="button"
                    onClick={() => setPayOpen(true)}
                    className="btn btn-secondary btn-sm"
                  >
                    Оплачен
                  </button>
                )}
              </div>
              <div style={{ fontSize: 13, color: '#1C3D2E' }}>
                Сумма: <strong>{(data.total || data.amount).toLocaleString('ru-RU')} ₽</strong>
              </div>

              {/* Inline-форма приёма оплаты — общий компонент с OrderModal */}
              <InlinePaymentDialog
                open={payOpen}
                busy={busy}
                onClose={() => setPayOpen(false)}
                title="Ручная оплата сертификата"
                onConfirm={async ({ method, paid_at, comment }) => {
                  setBusy(true);
                  try {
                    await api.markCertificatePaid(certId, { method, paid_at, comment: comment || undefined });
                    showToast('Сертификат помечен оплаченным, картинка отправляется в чат');
                    setPayOpen(false);
                    await refresh();
                    onUpdated?.();
                  } finally {
                    setBusy(false);
                  }
                }}
              />
            </div>

            {/* ── СЕРТИФИКАТ ────────────────────────────────────────── */}
            <Section label="Сертификат">
              <KV label="Код" value={
                data.code
                  ? <code style={{ fontSize: 14, fontWeight: 600, letterSpacing: 0.5 }}>{data.code}</code>
                  : <span style={{ color: '#999', fontStyle: 'italic' }}>генерируется при оплате</span>
              } />
              {/* «Действует до» показывается в блоке «Активация» — здесь дублировать не нужно */}
              {data.sent_to_buyer_at && <KV label="Отправлен в чат" value={fmt(data.sent_to_buyer_at)} />}

              {/* Поля «От кого / Получатель / Поздравление» — каждое со своим
                  карандашиком. По умолчанию read-only; клик по карандашу → input.
                  Редактирование разрешено только в status='active'. */}
              <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${BORDER}`, display: 'grid', gap: 8 }}>
                {/* От кого */}
                <FieldRow
                  label="От кого"
                  value={data.sender_name}
                  editing={editing.has('sender_name')}
                  editValue={edit.sender_name}
                  onEditChange={(v) => handleEditChange('sender_name', v)}
                  onTogglePencil={() => togglePencil('sender_name')}
                  editable={editable}
                />
                {/* Кому */}
                <FieldRow
                  label="Кому"
                  value={data.recipient_name}
                  editing={editing.has('recipient_name')}
                  editValue={edit.recipient_name}
                  onEditChange={(v) => handleEditChange('recipient_name', v)}
                  onTogglePencil={() => togglePencil('recipient_name')}
                  editable={editable}
                />

                {/* Поздравление — открытка с конвертиком как в карточке заказа.
                    Карандаш стоит сразу после слова «Поздравление», открытка
                    отрисовывается всегда (даже если пусто — placeholder).
                    В режиме edit внутри открытки — textarea. */}
                <div style={{ padding: '10px 14px', background: '#DFF2E8', border: '1px solid #1C3D2E', '--ch': '8px', '--stroke': '#1C3D2E', fontSize: 14, color: '#1C3D2E' }}>
                  <div style={{ fontWeight: 700, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <img src="/icons/mail.svg" alt="" style={{ width: 16, height: 16 }} />
                    <span>Поздравление</span>
                    {editable && (
                      <PencilButton
                        active={editing.has('message')}
                        onClick={() => togglePencil('message')}
                        title={editing.has('message') ? 'Отменить' : 'Редактировать'}
                      />
                    )}
                  </div>
                  {editing.has('message') ? (
                    <>
                      <textarea
                        value={edit.message}
                        onChange={(e) => handleEditChange('message', e.target.value.slice(0, 300))}
                        rows={3}
                        style={{
                          width: '100%', boxSizing: 'border-box',
                          padding: '8px 10px', border: `1px solid ${BORDER}`,
                          '--ch': '5px', '--stroke': (BORDER), fontSize: 13, resize: 'vertical', fontFamily: 'inherit',
                          color: '#1C3D2E', background: '#fff', outline: 'none',
                        }}
                        placeholder="Текст открытки..."
                        autoFocus
                      />
                      <div style={{ fontSize: 11, color: GRAY_TEXT, textAlign: 'right', marginTop: 2 }}>
                        {edit.message.length}/300
                      </div>
                    </>
                  ) : data.message ? (
                    <div style={{ color: '#1C3D2E', lineHeight: 1.4, wordBreak: 'break-word', overflowWrap: 'break-word', fontStyle: 'italic' }}>
                      «{data.message}»
                    </div>
                  ) : (
                    <div style={{ color: GRAY_TEXT, fontStyle: 'italic', fontSize: 13 }}>—</div>
                  )}
                </div>

                {editDirty && (
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button onClick={cancelAllEdits} disabled={busy} className="btn btn-secondary btn-sm">
                      Отменить
                    </button>
                    <button onClick={handleSaveEdit} disabled={busy} className="btn btn-primary btn-sm">
                      Сохранить
                    </button>
                  </div>
                )}

                {/* История изменений админом (sender_name / recipient_name / message) */}
                {history.length > 0 && (
                  <div style={{ marginTop: 6, paddingTop: 6, borderTop: `1px dashed ${BORDER}` }}>
                    <div style={{ fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
                      История изменений
                    </div>
                    <div style={{ display: 'grid', gap: 4 }}>
                      {history.map(h => (
                        <div key={h.id} style={{ fontSize: 12, color: '#555', lineHeight: 1.4 }}>
                          <span style={{ color: '#999' }}>{fmt(h.created_at)}</span>
                          {h.employee_name && <span style={{ color: '#999' }}> · {h.employee_name}</span>}
                          <span> · {FIELD_LABEL[h.field] || h.field}: </span>
                          <span style={{ color: '#888', textDecoration: 'line-through' }}>«{h.value_before || '—'}»</span>
                          <span> → </span>
                          <span style={{ color: '#222' }}>«{h.value_after || '—'}»</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Section>

            {/* ── ОТМЕНА — маленькая inline-строка как в OrderModal:328-332 ── */}
            {data.status === 'cancelled' && data.cancellation_reason && (
              <div style={{ marginTop: 6, padding: '6px 10px', background: '#FFF4F2', '--ch': '5px', '--stroke': '#FFCEC7', border: '1px solid #FFCEC7' }}>
                <div style={{ fontSize: 12, color: '#CB4D45' }}>
                  <strong>Причина:</strong> {data.cancellation_reason}
                  {data.cancelled_at && <span style={{ marginLeft: 8, color: '#999' }}>· {fmt(data.cancelled_at)}</span>}
                </div>
              </div>
            )}

            {/* ── КОММЕНТАРИЙ АДМИНИСТРАТОРА — нейтральный кремовый блок (как «Оплата» в заказе) ── */}
            <div style={{ padding: '12px 14px', background: '#F4F4EF', '--ch': '8px', borderLeft: `3px solid ${GRAY_TEXT}` }}>
              <div style={{
                fontSize: 11, color: GRAY_TEXT, fontWeight: 700,
                marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px',
              }}>
                Комментарий
              </div>
              <textarea
                value={adminComment}
                onChange={(e) => setAdminComment(e.target.value)}
                placeholder="Заметка по сертификату..."
                rows={3}
                style={{
                  width: '100%', boxSizing: 'border-box',
                  padding: '8px 10px', border: `1px solid ${BORDER}`,
                  '--ch': '5px', '--stroke': (BORDER), fontSize: 13, resize: 'vertical', fontFamily: 'inherit',
                  outline: 'none', background: '#fff',
                }}
              />
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-start', marginTop: 8 }}>
                <button
                  onClick={handleSaveAdminComment}
                  disabled={busy || !adminCommentDirty}
                  className="btn btn-primary btn-sm">
                  Сохранить комментарий
                </button>
                {adminCommentDirty && (
                  <button
                    onClick={() => setAdminComment(adminCommentSaved)}
                    disabled={busy} className="btn btn-secondary btn-sm">
                    Отменить
                  </button>
                )}
              </div>
            </div>

            {/* Информационные подсказки (статусы) */}
            {data.status === 'activated' && (
              <div style={{ fontSize: 12, color: GRAY_TEXT, textAlign: 'center', padding: '4px 0' }}>
                Сертификат уже активирован — отмена возможна только через списание со счёта {BRAND_NAME} пользователя.
              </div>
            )}
            {data.status === 'pending_payment' && (
              <div style={{ fontSize: 12, color: GRAY_TEXT, textAlign: 'center', padding: '4px 0' }}>
                Редактирование полей доступно только после оплаты сертификата.
              </div>
            )}
            {!isPaid && data.status === 'cancelled' && (
              <div style={{ fontSize: 12, color: GRAY_TEXT, textAlign: 'center', padding: '4px 0' }}>
                Сертификат был отменён без оплаты.
              </div>
            )}
          </div>
        )}

        {/* Модалка отмены с причиной */}
        {cancelOpen && (
          <div style={modalOverlayStyle} onClick={(e) => { if (e.target === e.currentTarget) setCancelOpen(false); }}>
            <div style={modalContentStyle}>
              <h3 style={{ marginTop: 0 }}>Отозвать сертификат?</h3>
              <p style={{ color: '#666', fontSize: 13, marginBottom: 12 }}>
                {data?.status === 'active'
                  ? 'Деньги клиенту нужно вернуть вручную через ЛК ЮКассы. Внутри системы зафиксируем причину.'
                  : 'Сертификат будет отменён, заказ перейдёт в финальный статус.'}
              </p>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Причина отмены (обязательно)"
                rows={3}
                style={{ width: '100%', boxSizing: 'border-box', padding: 10, fontSize: 14, border: `1px solid ${BORDER}`, '--ch': '6px', '--stroke': (BORDER), fontFamily: 'inherit', resize: 'vertical' }}
                autoFocus
              />
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
                <button onClick={() => { setCancelOpen(false); setCancelReason(''); }} disabled={busy} className="btn btn-secondary btn-sm">
                  Отмена
                </button>
                <button onClick={handleConfirmCancel} disabled={busy || !cancelReason.trim()} className="btn btn-danger btn-sm">
                  Подтвердить отзыв
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Inline-форма приёма оплаты вынесена внутрь блока «Оплата» (см. выше).
            Старая модалка-overlay удалена — теперь используется общий InlinePaymentDialog. */}
        {clientModalId && (
          <ClientProfileModal clientId={clientModalId} onClose={() => setClientModalId(null)} />
        )}
      </>
    </Modal>
  );
}

const editInputStyle = {
  width: '100%', boxSizing: 'border-box', padding: '6px 10px',
  border: `1px solid ${BORDER}`, '--ch': '5px', '--stroke': (BORDER), fontSize: 14, fontFamily: 'inherit',
};

const labelStyle = { display: 'block', fontSize: 12, color: GRAY_TEXT, marginBottom: 4 };

// Кнопки — общие классы .btn-* (UX-аудит 25.10)

const modalOverlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
  display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1100,
};

const modalContentStyle = {
  background: '#fff', '--ch': '8px', padding: 20, width: 'min(480px, 90vw)',
};

function CertificatesPage({ showToast }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  // Multi-select статус: массив выбранных значений (пусто = все)
  const [statusFilter, setStatusFilter] = useState([]);
  const [codeFilter, setCodeFilter] = useState('');
  const [codeSearchInHeader, setCodeSearchInHeader] = useState(false);  // показать input в шапке
  const [selectedId, setSelectedId] = useState(null);
  const codeSearchRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    // Backend ожидает один статус — для multi-select фильтруем на клиенте.
    // Если выбран ровно один — отдаём backend для оптимизации, иначе грузим всё.
    const apiStatus = statusFilter.length === 1 ? statusFilter[0] : undefined;
    api.listCertificates({ status: apiStatus, code: codeFilter || undefined, limit: 200 })
      .then(d => {
        let list = d.items || [];
        // Клиентский фильтр для multi-select (>1 статуса)
        if (statusFilter.length > 1) {
          list = list.filter(c => statusFilter.includes(c.status));
        }
        setItems(list);
        setTotal(d.total || 0);
      })
      .catch(e => showToast(e.message || 'Ошибка', 'error'))
      .finally(() => setLoading(false));
  }, [statusFilter, codeFilter, showToast]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">
          Сертификаты {total > 0 && <span style={{ fontSize: 22, fontWeight: 500, color: GRAY_TEXT }}>({total})</span>}
        </h1>
      </div>

      {loading ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>№</th>
                <th style={{ position: 'relative' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, visibility: (codeSearchInHeader || codeFilter) ? 'hidden' : 'visible' }}>
                    Код
                    <button
                      onClick={() => { setCodeSearchInHeader(true); setTimeout(() => codeSearchRef.current?.focus(), 0); }}
                      title="Поиск по коду"
                      style={iconSearchBtn}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(28,61,46,0.08)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                      </svg>
                    </button>
                  </span>
                  {(codeSearchInHeader || codeFilter) && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)' }}>
                      <input
                        ref={codeSearchRef}
                        type="text"
                        placeholder="Код…"
                        value={codeFilter}
                        onChange={(e) => setCodeFilter(e.target.value.toUpperCase())}
                        onBlur={() => { if (!codeFilter) setCodeSearchInHeader(false); }}
                        style={tableFilterInput}
                      />
                      <button
                        onClick={() => { setCodeFilter(''); setCodeSearchInHeader(false); }}
                        title="Сбросить"
                        style={tableFilterClear}
                      >×</button>
                    </span>
                  )}
                </th>
                <th>Сумма</th>
                <th>
                  <HeaderSelect
                    label="Статус сертификата"
                    value={statusFilter}
                    options={STATUS_OPTIONS}
                    onChange={setStatusFilter}
                    multi
                  />
                </th>
                <th>Статус оплаты</th>
                <th>Клиент</th>
                <th>Создан</th>
                <th>Истекает</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={8} style={{ padding: 32, textAlign: 'center', color: GRAY_TEXT }}>Сертификаты не найдены</td></tr>
              ) : items.map(c => {
                const pBadge = PAYMENT_BADGE[c.payment_status] || { text: c.payment_status || '—', cls: 'pending' };
                const pIcon = paymentIconFor(c.payment_status);
                const cBadge = getCertStatusBadge(c);
                const buyer = c.purchased_by || {};
                const cPlatform = c.source_platform
                  || (buyer.telegram_id ? 'telegram' : buyer.max_id ? 'max' : 'web');
                const cPlatformLabel = cPlatform === 'telegram' ? 'TG' : cPlatform === 'max' ? 'Max' : 'Web';
                const cPlatformClass = cPlatform === 'telegram' ? 'platform-tg' : cPlatform === 'max' ? 'platform-max' : 'platform-web';
                const cPlatformIcon = cPlatform === 'telegram' ? 'tg' : cPlatform === 'max' ? 'max' : 'web';
                return (
                  <tr key={c.id}
                    onClick={() => setSelectedId(c.id)}
                    style={{ cursor: 'pointer' }}>
                    <td style={{ whiteSpace: 'nowrap' }}>{c.cert_number || c.id}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{c.code ? <code style={{ fontSize: 12, fontWeight: 600 }}>{c.code}</code> : <span style={{ color: '#C1C8C4' }}>—</span>}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{c.amount.toLocaleString('ru-RU')} ₽</td>
                    <td>
                      {cBadge ? (
                        <span className={`badge badge-${cBadge.cls}`}>
                          {cBadge.icon && <img src={cBadge.icon} alt="" />}
                          {cBadge.text}
                        </span>
                      ) : (
                        <span style={{ color: '#C1C8C4' }}>—</span>
                      )}
                    </td>
                    <td>
                      <span className={`badge badge-${pBadge.cls}`}>
                        {pIcon && <img src={pIcon} alt="" />}
                        {(() => {
                          const isPaid = c.payment_status === 'paid' || c.payment_status === 'paid_chat';
                          // Если оплачен — показываем способ оплаты (QR / Карта / Наличные / Р/с) — как в OrdersPage:588-593.
                          if (isPaid) {
                            const pm = (c.payment_method || '').replace(/\s*\(.*\)\s*$/, '').trim();
                            const pmLabel = pm ? paymentMethodLabel(pm) : null;
                            return pmLabel ? `Оплачен · ${pmLabel}` : 'Оплачен';
                          }
                          // Таймер ожидания внутри того же бейджа — как в OrdersPage:600-605.
                          if (c.payment_status === 'pending' && c.payment_url_generated_at) {
                            const created = new Date(c.payment_url_generated_at).getTime();
                            const expire = created + PAYMENT_EXPIRE_MINUTES * 60 * 1000;
                            const remainingMs = expire - Date.now();
                            if (remainingMs > 0) {
                              const mins = Math.ceil(remainingMs / 60000);
                              return `${pBadge.text} · ${mins} мин`;
                            }
                            return `${pBadge.text} · истекает`;
                          }
                          return pBadge.text;
                        })()}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span>{[buyer.first_name, buyer.last_name].filter(Boolean).join(' ').trim() || '—'}</span>
                        <span className={`platform-badge ${cPlatformClass}`}>
                          <img src={`/icons/nv-${cPlatformIcon}.svg`} alt="" />
                          {cPlatformLabel}
                        </span>
                      </div>
                    </td>
                    <td>{fmt(c.created_at)}</td>
                    <td>{fmt(c.expires_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selectedId && (
        <CertificateDetailModal
          certId={selectedId}
          onClose={() => setSelectedId(null)}
          showToast={showToast}
          onUpdated={load}
        />
      )}
    </div>
  );
}

const tableFilterSelect = {
  padding: '4px 8px', fontSize: 12, fontFamily: 'inherit',
  border: `1px solid ${BORDER}`, '--ch': '5px', '--stroke': (BORDER), background: '#fff',
  color: '#1C3D2E', cursor: 'pointer',
};
const iconSearchBtn = {
  width: 28, height: 28, padding: 0, border: 'none',
  background: 'transparent', cursor: 'pointer',
  color: BRAND_GREEN, display: 'inline-flex',
  alignItems: 'center', justifyContent: 'center',
  '--ch': '5px', transition: 'background 0.15s',
};
const tableFilterInput = {
  padding: '4px 8px', fontSize: 12, fontFamily: 'inherit',
  border: `1px solid ${BORDER}`, '--ch': '5px', '--stroke': (BORDER), outline: 'none',
  textTransform: 'uppercase', width: 130,
};
const tableFilterClear = {
  width: 18, height: 18, border: 'none',
  background: BRAND_GREEN, color: '#fff',
  borderRadius: '50%', cursor: 'pointer', fontSize: 12, lineHeight: 1,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
};

export default CertificatesPage;
